// PrimaryTime.tsx - Pagrindinis laiko intervalo ir indikatorių konfigūracijos komponentas

// ===== BLOKAS 1 PrimaryTime.tsx: IMPORTAI =====
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Info, AlertCircle, ChevronRight, X, Settings, SlidersHorizontal, ChevronDown, ChevronUp, Check
} from 'lucide-react';
import { TimeInterval } from './TimeInterval';
import { cn } from '../../utils/cn';
import { toast } from 'sonner';
import { Tooltip } from '../../ui/Tooltip';
import { useAuth } from '../../contexts/AuthContext';

// ===== BLOKAS 2 PrimaryTime.tsx: INTERFEISAI IR TIPAI =====
// === SUB-BLOKAS 2.1: Safe helpers ===
const upper = (v: unknown) =>
  typeof v === 'string' ? v.toUpperCase() : '';

// === SUB-BLOKAS 2.2: Pagrindiniai interfeisai ===
interface PrimaryTimeProps {
  botId?: string;
  selectedInterval: string;
  onIntervalChange: (interval: string) => void;
  initialIndicators: any[];
  onIndicatorsChange: (indicators: any[]) => void;
}

interface Indicator {
  id: string;                 // UI id (jei nėra – sugeneruosim)
  type: string;               // pvz., 'sma', 'macd'
  label?: string;             // rodomas pavadinimas
  settings?: Record<string, any>; // indikatorius parametrai
}

// ===== BLOKAS 3 PrimaryTime.tsx: PAGALBINĖS FUNKCIJOS IR KONSTANTOS =====
// === SUB-BLOKAS 3.1: humanize funkcija ===
function humanize(key: string) {
  const s = key.replace(/[_-]+/g, ' ').trim();
  if (!s) return 'Indicator';
  return s
    .split(' ')
    .map(w => (w.length <= 3 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

// === SUB-BLOKAS 3.2: normalizeIndicators funkcija ===
console.log(`🔧 [BLOKAS 3.2 PrimaryTime.tsx] Normalizuojami indikatoriai`);
function normalizeIndicators(input: any): Indicator[] {
  // jei ateina kaip masyvas objektų
  if (Array.isArray(input)) {
    return input.map((it: any, idx: number) => {
      const type = String(it?.type ?? it?.key ?? 'custom');

      // Extract settings: use it.settings if exists, otherwise extract all non-standard fields
      let settings = it?.settings ?? it?.params ?? {};

      // If no explicit settings object, extract parameter fields (period, fastPeriod, etc.)
      if (Object.keys(settings).length === 0) {
        const { id, type: _, key, label, ...rest } = it;
        settings = rest; // All remaining fields become settings
      }

      return {
        id: it?.id ?? crypto.randomUUID(), // Ensure unique ID for UI
        type,
        label: it?.label ?? humanize(type),
        settings,
      };
    });
  }
  // jei ateina kaip { types: ['sma','macd',...], count: N }
  if (input && Array.isArray(input.types)) {
    return input.types.map((t: any, idx: number) => ({
      id: crypto.randomUUID(), // Ensure unique ID for UI
      type: String(t),
      label: humanize(String(t)),
      settings: {},
    }));
  }
  return [];
}

// === SUB-BLOKAS 3.3: availableIndicators konstantos ===
const availableIndicators = [
  { value: 'rsi', label: 'RSI (Relative Strength Index)', defaultSettings: { period: 14, overbought: 70, oversold: 30 } },
  { value: 'macd', label: 'MACD', defaultSettings: { fastPeriod: 12, slowPeriod: 26, signalPeriod: 9 } },
  { value: 'bb', label: 'Bollinger Bands', defaultSettings: { period: 20, std: 2 } },
  { value: 'sma', label: 'Simple Moving Average', defaultSettings: { period: 20 } },
  { value: 'ema', label: 'Exponential Moving Average', defaultSettings: { period: 20 } },
  { value: 'adx', label: 'ADX (Average Directional Index)', defaultSettings: { period: 14, trendStrength: 25 } },
  { value: 'volume', label: 'Volume', defaultSettings: { period: 20 } },
  { value: 'momentum', label: 'Momentum Oscillator', defaultSettings: { period: 10 } },

  // Conceptual indicators (no numerical settings)
  { value: 'price_action', label: 'Price Action', defaultSettings: {} },
  { value: 'level2_data', label: 'Level 2 Data', defaultSettings: {} },
  { value: 'fibonacci', label: 'Fibonacci Levels/Retracements', defaultSettings: {} },
  { value: 'pivot_points', label: 'Pivot Points', defaultSettings: {} },
  { value: 'candlestick', label: 'Candlestick Patterns', defaultSettings: {} },
  { value: 'support_resistance', label: 'Support/Resistance', defaultSettings: {} },
  { value: 'trend_lines', label: 'Trend Lines', defaultSettings: {} },
  { value: 'volume_profile', label: 'Volume Profile', defaultSettings: {} },
  { value: 'order_flow', label: 'Order Flow', defaultSettings: {} },
  { value: 'market_profile', label: 'Market Profile', defaultSettings: {} },
  { value: 'time_sales', label: 'Time and Sales', defaultSettings: {} }
];

// === SUB-BLOKAS 3.4: intervals konstantos ===
const intervals = ['1m', '3m', '5m', '15m', '30m', '1h', '4h', '8h', '1d', '1w', '1M'];

// ===== BLOKAS 4 PrimaryTime.tsx: PAGRINDINIS KOMPONENTAS =====
export const PrimaryTime: React.FC<PrimaryTimeProps> = ({
  botId,
  selectedInterval,
  onIntervalChange,
  initialIndicators,
  onIndicatorsChange
}) => {
  // === SUB-BLOKAS 4.1: State kintamieji ir ref ===
  console.log(`🔵 [BLOKAS 4.1 PrimaryTime.tsx] 📥 COMPONENT PROPS RECEIVED:`, {
    botId, selectedInterval, initialIndicators: initialIndicators ? {
      count: initialIndicators.length,
      types: initialIndicators.map(i => i.type || i.label)
    } : 'EMPTY_OR_NULL'
  });
  
  const { user } = useAuth();
  const [indicators, setIndicators] = useState<Indicator[]>(normalizeIndicators(initialIndicators));
  const [interval, setInterval] = useState<string>(selectedInterval || '5m');
  const [expandedIndicator, setExpandedIndicator] = useState<string | null>(null);
  const prevBotIdRef = React.useRef(botId);

  // Animation for when indicators are loaded from strategy
  const indicatorAnimation = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3 }
  };

  // === SUB-BLOKAS 4.2: useEffect (botId change) ===
  console.log(`🔄 [BLOKAS 4.2 PrimaryTime.tsx] BotId keičiamas efektas`);
  useEffect(() => {
    if (prevBotIdRef.current !== botId) {
      setIndicators(normalizeIndicators(initialIndicators));
      setExpandedIndicator(null);
      prevBotIdRef.current = botId;
    }
  }, [initialIndicators, botId]);

  // === SUB-BLOKAS 4.3: useEffect (interval change) ===
  console.log(`🔄 [BLOKAS 4.3 PrimaryTime.tsx] Interval keičiamas efektas`);
  useEffect(() => {
    setInterval(selectedInterval || '5m');
  }, [selectedInterval, botId]);

  // === SUB-BLOKAS 4.4: saveIndicators funkcija ===
  console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] saveIndicators funkcijos aprašas`);
  async function saveIndicators(next: Indicator[], removedIndicatorId?: string) {
    console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] saveIndicators called with count:`, Array.isArray(next) ? next.length : 'N/A');
    console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] botId:`, botId, 'user?.id:', user?.id);

    setIndicators(next);

    // DB stulpelyje laikom "švarius" įrašus: be UI-only "id"
    const payload = next.map(({ id, ...rest }) => rest);

    console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] Calling onIndicatorsChange with payload count:`, Array.isArray(payload) ? payload.length : 'N/A');

    // ✅ CRITICAL: ALWAYS update parent state (even if bot not saved yet!)
    if (typeof onIndicatorsChange === 'function') {
      onIndicatorsChange(payload as any); // Keep backwards compatible
      console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] ✅ Parent state updated`);
    }

    // ✅ Only autosave to DB if bot already exists
    if (!user?.id || !botId) {
      console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] ⚠️ No botId - skipping autosave (form state updated)`);
      return; // Skip autosave, but form state is already updated above
    }

    // Use global sendChange if available (DEV mode only) - only if bot exists
    if (window.sendChange) {
      window.sendChange('primaryTimeIndicators', payload);
      console.log(`💾 [BLOKAS 4.4 PrimaryTime.tsx] ✅ Autosave triggered`);
    }
  }

  useEffect(() => {
    if (user?.id && botId && selectedInterval) {
      // Update bot configuration when interval changes
      console.log(`🔄 [BLOKAS 4.3 PrimaryTime.tsx] 🔄 INTERVAL CHANGE DETECTED:`, {
        to: selectedInterval,
        botId: botId
      });
      
      // Use global sendChange if available
      if (window.sendChange) {
        window.sendChange('primaryTimeInterval', selectedInterval);
      }
    }
  }, [selectedInterval, user?.id, botId]);

  // === SUB-BLOKAS 4.5: handleAddIndicator funkcija ===
  console.log(`➕ [BLOKAS 4.5 PrimaryTime.tsx] handleAddIndicator funkcijos aprašas`);
  const handleAddIndicator = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const indicatorType = event.target.value;
    console.log(`➕ [BLOKAS 4.5 PrimaryTime.tsx] ➕ ADD INDICATOR STARTED:`, {
      indicatorType,
      currentIndicatorsCount: indicators.length,
      currentIndicators: indicators.map(i => i.type || i.label)
    });
    
    console.log(`➕ [BLOKAS 4.5 PrimaryTime.tsx] Starting to add indicator:`, indicatorType);
    
    if (!indicatorType) return;

    const indicator = availableIndicators.find(i => i.value === indicatorType);
    if (!indicator) {
      console.log(`➕ [BLOKAS 4.5 PrimaryTime.tsx] Indicator not found`);
      return;
    }

    // Check if indicator already exists
    const exists = indicators.some((ind: any) => ind.type === indicatorType);
    if (exists) {
      toast.error(`${indicator.label} is already added`);
      event.target.value = '';
      return;
    }
    
    // Convert defaultSettings to flat PTI format
    const flatSettings: Record<string, any> = { key: indicatorType };

    // Map settings to PTI format based on indicator type
    if (indicatorType === 'sma' || indicatorType === 'ema' || indicatorType === 'rsi' || indicatorType === 'adx' || indicatorType === 'momentum' || indicatorType === 'volume') {
      flatSettings.period = indicator.defaultSettings.period || 14;
      if (indicatorType === 'rsi' && indicator.defaultSettings.overbought !== undefined) {
        flatSettings.overbought = indicator.defaultSettings.overbought;
        flatSettings.oversold = indicator.defaultSettings.oversold;
      }
      if (indicatorType === 'adx' && indicator.defaultSettings.trendStrength !== undefined) {
        flatSettings.trendStrength = indicator.defaultSettings.trendStrength;
      }
    } else if (indicatorType === 'macd') {
      flatSettings.fast = indicator.defaultSettings.fastPeriod || 12;
      flatSettings.slow = indicator.defaultSettings.slowPeriod || 26;
      flatSettings.signal = indicator.defaultSettings.signalPeriod || 9;
    } else if (indicatorType === 'bb') {
      flatSettings.period = indicator.defaultSettings.period || 20;
      flatSettings.std = indicator.defaultSettings.std || 2;
    }

    const newIndicator: Indicator = {
      id: crypto.randomUUID(),
      type: indicatorType,
      settings: flatSettings,
      label: indicator.label
    };

    const updatedIndicators = [...indicators, newIndicator];
    console.log(`➕ [BLOKAS 4.5 PrimaryTime.tsx] Created new indicator, updating state immediately`);
    
    console.log(`➕ [BLOKAS 4.5 PrimaryTime.tsx] 📤 CALLING onIndicatorsChange WITH:`, {
      newIndicatorsCount: updatedIndicators.length,
      newIndicators: updatedIndicators.map(i => ({ type: i.type, label: i.label }))
    });
    
    // INDICATORS LOGIC FIX: Update parent state first
    await saveIndicators(updatedIndicators); // Use saveIndicators to update state and DB
    
    toast.success(`Added ${indicator.label} to primary timeframe`);
    
    // Reset dropdown
    event.target.value = '';
  };

  // === SUB-BLOKAS 4.6: handleRemoveIndicator funkcija ===
  console.log(`➖ [BLOKAS 4.6 PrimaryTime.tsx] handleRemoveIndicator funkcijos aprašas`);
  const handleRemoveIndicator = async (id: string) => {
    console.log(`➖ [BLOKAS 4.6 PrimaryTime.tsx] ➖ REMOVE INDICATOR STARTED:`, {
      indicatorId: id, currentIndicatorsCount: indicators.length
    });

    console.log(`➖ [BLOKAS 4.6 PrimaryTime.tsx] Starting to remove indicator:`, id);


    const updatedIndicators = indicators.filter(i => i.id !== id);

    console.log(`➖ [BLOKAS 4.6 PrimaryTime.tsx] 📤 CALLING onIndicatorsChange WITH:`, {
      newIndicatorsCount: updatedIndicators.length,
      removedIndicatorId: id
    });

    console.log(`➖ [BLOKAS 4.6 PrimaryTime.tsx] Filtered indicators, remaining:`, updatedIndicators.length);

    // ✅ INDICATORS LOGIC FIX: Send full updated array with removed ID
    await saveIndicators(updatedIndicators, id);

    toast.success('Primary indicator removed');

    if (updatedIndicators.length === 0) {
      toast.info('All primary indicators removed.');
    }

    if (expandedIndicator === id) {
      setExpandedIndicator(null);
    }
  };

  // === SUB-BLOKAS 4.7: handleToggleExpand ir handleUpdateSettings ===
  console.log(`⚙️ [BLOKAS 4.7 PrimaryTime.tsx] handleToggleExpand ir handleUpdateSettings funkcijos`);
  const handleToggleExpand = (id: string) => {
    setExpandedIndicator(prev => prev === id ? null : id);
  };

  const handleUpdateSettings = async (id: string, settings: Record<string, any>) => {
    console.log(`⚙️ [BLOKAS 4.7 PrimaryTime.tsx] ⚙️ UPDATE SETTINGS STARTED:`, {
      indicatorId: id,
      newSettings: settings
    });
    
    console.log(`⚙️ [BLOKAS 4.7 PrimaryTime.tsx] Starting to update settings for:`, id);
    

    // Process settings - ensure numeric values are properly parsed
    const processedSettings: Record<string, any> = {};
    for (const key in settings) {
      const value = settings[key];
      if (typeof value === 'string') {
        if (value === '') {
          processedSettings[key] = 0;
        } else if (!isNaN(Number(value))) {
          processedSettings[key] = Number(value);
        } else {
          processedSettings[key] = value;
        }
      } else {
        processedSettings[key] = value;
      }
    }
    
    const updatedIndicators = indicators.map(i => i.id === id ? { ...i, settings: processedSettings } : i);
    console.log(`⚙️ [BLOKAS 4.7 PrimaryTime.tsx] Settings processed, updating state`);
    
    console.log(`⚙️ [BLOKAS 4.7 PrimaryTime.tsx] 📤 CALLING onIndicatorsChange WITH UPDATED SETTINGS:`, {
      indicatorId: id,
      updatedIndicatorsCount: updatedIndicators.length
    });
    
    await saveIndicators(updatedIndicators); // Use saveIndicators to update state and DB
  };

  // === SUB-BLOKAS 4.8: Helper funkcijos ===
  console.log(`🛠️ [BLOKAS 4.8 PrimaryTime.tsx] Helper funkcijos (getIndicatorDisplayName, hasConfigurableSettings)`);
  // Get display name for indicator
  const getIndicatorDisplayName = (indicator: Indicator) => {
    // If the indicator has a label from the strategy, use it
    if (indicator.label) {
      return indicator.label;
    }
    
    // Otherwise, find the indicator in the available indicators list
    const foundIndicator = availableIndicators.find(i => i.value === indicator.type);
    return foundIndicator ? foundIndicator.label : upper(indicator?.type);
  };

  // Check if indicator has configurable settings
  const hasConfigurableSettings = (indicatorType: string) => {
    const indicator = availableIndicators.find(i => i.value === indicatorType);
    if (!indicator) return false;
    
    // Check if the indicator has any default settings
    return Object.keys(indicator.defaultSettings).length > 0;
  };

  // === SUB-BLOKAS 4.9: renderIndicatorSettings funkcija ===
  console.log(`🎨 [BLOKAS 4.9 PrimaryTime.tsx] renderIndicatorSettings funkcijos aprašas`);
  function renderIndicatorSettings(indicator: Indicator) {
    const indicatorConfig = availableIndicators.find(i => i.value === indicator.type);
    if (!indicatorConfig) return null;

    if (Object.keys(indicatorConfig.defaultSettings).length === 0) {
      return (
        <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
          No configurable settings for this indicator.
        </p>
      );
    }

    const inputClass = "w-full px-2.5 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 no-spinner";
    const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1";
    const stopPropagation = (e: React.MouseEvent | React.KeyboardEvent) => e.stopPropagation();

    const s = indicator.settings || {};

    switch (indicator.type) {
      case 'rsi':
        return (
          <div className="space-y-3 pt-2" onClick={stopPropagation}>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Period</label>
                <input
                  type="number"
                  value={s.period ?? 14}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    period: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Overbought</label>
                <input
                  type="number"
                  value={s.overbought ?? 70}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    overbought: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Oversold</label>
                <input
                  type="number"
                  value={s.oversold ?? 30}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    oversold: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        );

      case 'macd': {
        const fast = s.fastPeriod ?? s.fast ?? 12;
        const slow = s.slowPeriod ?? s.slow ?? 26;
        const signal = s.signalPeriod ?? s.signal ?? 9;
        return (
          <div className="pt-2" onClick={stopPropagation}>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Fast</label>
                <input
                  type="number"
                  value={fast}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    fastPeriod: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Slow</label>
                <input
                  type="number"
                  value={slow}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    slowPeriod: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Signal</label>
                <input
                  type="number"
                  value={signal}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    signalPeriod: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
            </div>
          </div>
        );
      }

      case 'bollinger':
        return (
          <div className="pt-2" onClick={stopPropagation}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Period</label>
                <input
                  type="number"
                  value={s.period ?? 20}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    period: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Std Dev</label>
                <input
                  type="number"
                  value={s.stdDev ?? 2}
                  onChange={(e) => handleUpdateSettings(indicator.id, {
                    ...s,
                    stdDev: e.target.value === '' ? 0 : Number(e.target.value)
                  })}
                  onKeyDown={stopPropagation}
                  className={inputClass}
                  step="0.1"
                />
              </div>
            </div>
          </div>
        );

      case 'adx':
      case 'sma':
      case 'ema':
      case 'volume':
      case 'momentum':
        return (
          <div className="pt-2" onClick={stopPropagation}>
            <div className="max-w-[120px]">
              <label className={labelClass}>Period</label>
              <input
                type="number"
                value={s.period ?? 14}
                onChange={(e) => handleUpdateSettings(indicator.id, {
                  ...s,
                  period: e.target.value === '' ? 0 : Number(e.target.value)
                })}
                onKeyDown={stopPropagation}
                className={inputClass}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  // ===== BLOKAS 5 PrimaryTime.tsx: JSX GRAŽINIMAS =====
  console.log(`🎯 [BLOKAS 5 PrimaryTime.tsx] JSX grąžinimo pradžia`);
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Primary Time Interval</h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">Configure the main timeframe for trading decisions</p>
          </div>
          <div className="h-12 w-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
            <Clock className="h-6 w-6 text-blue-500" />
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Primary Time Interval
              </label>
              <Tooltip 
                content={<div className="space-y-4">
                  <div>
                    <h5 className="font-medium text-blue-400 mb-2">Primary Time Interval</h5>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-blue-400 mb-1">Purpose</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Main timeframe for trading decisions
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Determines signal frequency
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Sets trading rhythm
                          </li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-blue-400 mb-1">Selection Guidelines</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Match your trading style
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Consider market volatility
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Account for trading costs
                          </li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-blue-400 mb-1">Recommended Intervals</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Scalping: 1m - 5m
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Day Trading: 15m - 1h
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Swing Trading: 4h - 1d
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                    <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-300 text-xs">
                      Choose a timeframe that matches your trading style and availability. Lower timeframes require more active management but offer more opportunities.
                    </p>
                  </div>
                </div>}
                position="left"
              >
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>

            <TimeInterval
              intervals={intervals}
              selectedInterval={selectedInterval}
              onIntervalChange={onIntervalChange}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Primary Timeframe Indicators
              </label>
              <Tooltip 
                content={<div className="space-y-4">
                  <div>
                    <h5 className="font-medium text-blue-400 mb-2">Primary Timeframe Indicators</h5>
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium text-blue-400 mb-1">Purpose</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Generate trading signals
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Identify market conditions
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Measure momentum and trends
                          </li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-blue-400 mb-1">Indicator Types</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Trend (Moving Averages)
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Momentum (RSI, MACD)
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Volatility (Bollinger Bands)
                          </li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium text-blue-400 mb-1">Best Practices</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Use complementary indicators
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Avoid indicator redundancy
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Optimize indicator settings
                          </li>
                        </ul>
                      </div>
                      
                      <div>
                        <p className="font-medium text-blue-400 mb-1">Manual Configuration</p>
                        <ul className="space-y-1 text-gray-300">
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Available when no strategy is selected
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Disabled when strategy is active
                          </li>
                          <li className="flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-blue-400 flex-shrink-0" />
                            Enable Signal Only Mode for custom indicators
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 bg-blue-900/20 p-2 rounded">
                    <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-blue-300 text-xs">
                      Combine different types of indicators to get a complete market picture. More indicators aren't always better - focus on quality over quantity.
                    </p>
                  </div>
                </div>}
                position="left"
              >
                <Info className="h-4 w-4 text-gray-400 cursor-help" />
              </Tooltip>
            </div>

            <div className="space-y-4">

              {indicators.length === 0 && (
                <div className="text-center py-8 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <SlidersHorizontal className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No indicators yet
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Add indicators to define your trading signals
                  </p>
                </div>
              )}

              {indicators.map((indicator, index) => {
                const isExpanded = expandedIndicator === indicator.id;
                const hasSettings = hasConfigurableSettings(indicator.type);

                return (
                  <motion.div
                    key={indicator.id}
                    {...indicatorAnimation}
                    transition={{ delay: index * 0.05, ...indicatorAnimation.transition }}
                    className={cn(
                      "rounded-lg border overflow-hidden",
                      "bg-gray-50 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600",
                      isExpanded && "ring-2 ring-blue-500/30"
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-center justify-between p-3",
                        hasSettings && "cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600/50 transition-colors"
                      )}
                      onClick={() => hasSettings && handleToggleExpand(indicator.id)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">
                          {(indicator?.key ?? indicator?.type ?? 'indicator').toUpperCase()}
                        </span>
                        {hasSettings && indicator.settings && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {indicator.type === 'macd'
                              ? `(${indicator.settings.fastPeriod ?? indicator.settings.fast ?? 12}/${indicator.settings.slowPeriod ?? indicator.settings.slow ?? 26}/${indicator.settings.signalPeriod ?? indicator.settings.signal ?? 9})`
                              : indicator.settings.period
                                ? `(${indicator.settings.period})`
                                : ''
                            }
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {hasSettings && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleExpand(indicator.id);
                            }}
                            className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                            title={isExpanded ? "Collapse settings" : "Edit settings"}
                          >
                            {isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-blue-500" />
                            ) : (
                              <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveIndicator(indicator.id);
                          }}
                          className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors group"
                          title="Remove indicator"
                        >
                          <X className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-red-500" />
                        </button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && hasSettings && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="px-3 pb-3 pt-1 border-t border-gray-200 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50">
                            {renderIndicatorSettings(indicator)}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>

            <div className="relative">
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                onChange={handleAddIndicator}
                value=""
              >
                <option value="" disabled>
                  Add an indicator
                </option>
                {availableIndicators.map((indicator) => (
                  <option key={indicator.value} value={indicator.value}>
                    {indicator.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <SlidersHorizontal className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>

    </motion.div>
  );
}; 

export default PrimaryTime;